import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  Phone,
  Building2,
  RefreshCw,
  Trash2,
  Loader2,
  ExternalLink,
  Inbox,
  CheckCheck,
  X,
} from 'lucide-react';
import './AdminEnquiries.css';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const INTEREST_LABEL = {
  shake_up: 'Social Shake Up (Consultancy)',
  studio_services: 'Studio Services',
  podcast: 'Podcast Management',
  tool: 'Social Week Tool',
  beta: 'Social Week DIY (Beta)',
  managed: 'Fully Managed',
  not_sure: 'Not sure yet',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminEnquiries() {
  const [loading, setLoading] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, contacted: 0, won: 0, lost: 0 });
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? `${API}/api/enquiries`
        : `${API}/api/enquiries?status=${filter}`;
      const res = await axios.get(url);
      setEnquiries(res.data.enquiries || []);
      setCounts(res.data.counts || { total: 0, new: 0, contacted: 0, won: 0, lost: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load enquiries');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    setUpdatingId(id);
    try {
      await axios.patch(`${API}/api/enquiries/${id}`, { status });
      toast.success('Status updated');
      await load();
      if (selected?.id === id) setSelected((s) => s ? { ...s, status } : s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setUpdatingId(selected.id);
    try {
      await axios.patch(`${API}/api/enquiries/${selected.id}`, { notes: notesDraft });
      toast.success('Notes saved');
      setSelected((s) => s ? { ...s, notes: notesDraft } : s);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this enquiry? This cannot be undone.')) return;
    setUpdatingId(id);
    try {
      await axios.delete(`${API}/api/enquiries/${id}`);
      toast.success('Enquiry deleted');
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const openDetail = (enq) => {
    setSelected(enq);
    setNotesDraft(enq.notes || '');
  };

  return (
    <div className="enquiries-shell" data-testid="enquiries-shell">
      <div className="enquiries-header">
        <div>
          <h2 className="enquiries-title">
            <Inbox size={20} /> NicheSocial Enquiries
          </h2>
          <p className="enquiries-sub">
            Leads from the <code>/nichesocial</code> landing page. Reply to{' '}
            <strong>jon.hawkins@nichecom.co.uk</strong> within one working day.
          </p>
        </div>
        <button className="enquiries-refresh" onClick={load} data-testid="enquiries-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="enquiries-stats" data-testid="enquiries-stats">
        {[
          { key: 'all', label: 'All', count: counts.total },
          { key: 'new', label: 'New', count: counts.new },
          { key: 'contacted', label: 'Contacted', count: counts.contacted },
          { key: 'won', label: 'Won', count: counts.won },
          { key: 'lost', label: 'Lost', count: counts.lost },
        ].map((s) => (
          <button
            key={s.key}
            className={`enquiries-stat ${filter === s.key ? 'active' : ''} ${s.key !== 'all' ? `enquiries-stat-${s.key}` : ''}`}
            onClick={() => setFilter(s.key)}
            data-testid={`enquiries-filter-${s.key}`}
          >
            <span className="enquiries-stat-count">{s.count}</span>
            <span className="enquiries-stat-label">{s.label}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="enquiries-loading"><Loader2 size={18} className="enquiries-spin" /> Loading enquiries…</div>
      ) : enquiries.length === 0 ? (
        <div className="enquiries-empty" data-testid="enquiries-empty">
          <Inbox size={32} />
          <h3>No enquiries yet</h3>
          <p>When leads submit the form on <code>/nichesocial</code> they’ll appear here.</p>
        </div>
      ) : (
        <div className="enquiries-table-wrap" data-testid="enquiries-table">
          <table className="enquiries-table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Name</th>
                <th>Agency</th>
                <th>Interest</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((e) => (
                <tr key={e.id} className={`enq-row enq-row-${e.status}`} data-testid={`enq-row-${e.id}`}>
                  <td className="enq-date">{formatDate(e.created_at)}</td>
                  <td className="enq-name">
                    <button className="enq-name-btn" onClick={() => openDetail(e)} data-testid={`enq-open-${e.id}`}>
                      {e.name}
                    </button>
                    <span className="enq-email">{e.email}</span>
                  </td>
                  <td className="enq-agency">{e.agency_name || <span className="enq-muted">—</span>}</td>
                  <td>
                    <span className={`enq-interest enq-interest-${e.interest}`}>
                      {INTEREST_LABEL[e.interest] || e.interest}
                    </span>
                  </td>
                  <td>
                    <select
                      className={`enq-status-select enq-status-${e.status}`}
                      value={e.status}
                      onChange={(ev) => handleStatusChange(e.id, ev.target.value)}
                      disabled={updatingId === e.id}
                      data-testid={`enq-status-${e.id}`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="enq-actions">
                    <button
                      className="enq-action-btn"
                      onClick={() => openDetail(e)}
                      title="View details"
                      data-testid={`enq-view-${e.id}`}
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button
                      className="enq-action-btn enq-action-danger"
                      onClick={() => handleDelete(e.id)}
                      disabled={updatingId === e.id}
                      title="Delete"
                      data-testid={`enq-delete-${e.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="enq-modal-backdrop" onClick={() => setSelected(null)} data-testid="enq-modal-backdrop">
          <div className="enq-modal" onClick={(ev) => ev.stopPropagation()} data-testid="enq-modal">
            <button className="enq-modal-close" onClick={() => setSelected(null)} data-testid="enq-modal-close">
              <X size={18} />
            </button>
            <div className="enq-modal-header">
              <h3>{selected.name}</h3>
              <span className={`enq-pill enq-pill-${selected.status}`}>{STATUS_OPTIONS.find(s => s.value === selected.status)?.label}</span>
            </div>
            <div className="enq-modal-meta">
              <div><Mail size={13} /> <a href={`mailto:${selected.email}`}>{selected.email}</a></div>
              {selected.phone && <div><Phone size={13} /> <a href={`tel:${selected.phone}`}>{selected.phone}</a></div>}
              {selected.agency_name && <div><Building2 size={13} /> {selected.agency_name}</div>}
              <div className="enq-modal-meta-grey">Received {formatDate(selected.created_at)}</div>
            </div>
            <div className="enq-modal-field">
              <label>Interested in</label>
              <p className={`enq-interest enq-interest-${selected.interest}`}>{INTEREST_LABEL[selected.interest] || selected.interest}</p>
            </div>
            {selected.message && (
              <div className="enq-modal-field">
                <label>Their message</label>
                <p className="enq-modal-message">{selected.message}</p>
              </div>
            )}
            <div className="enq-modal-field">
              <label>Internal notes</label>
              <textarea
                rows={4}
                value={notesDraft}
                onChange={(ev) => setNotesDraft(ev.target.value)}
                placeholder="Add notes for the team…"
                data-testid="enq-modal-notes"
              />
            </div>
            <div className="enq-modal-actions">
              <button
                className="enq-btn enq-btn-primary"
                onClick={handleSaveNotes}
                disabled={updatingId === selected.id}
                data-testid="enq-modal-save"
              >
                {updatingId === selected.id ? <Loader2 size={13} className="enquiries-spin" /> : <CheckCheck size={13} />}
                Save notes
              </button>
              <a className="enq-btn enq-btn-ghost" href={`mailto:${selected.email}`} data-testid="enq-modal-mailto">
                <Mail size={13} /> Email them
              </a>
              <button
                className="enq-btn enq-btn-danger"
                onClick={() => handleDelete(selected.id)}
                disabled={updatingId === selected.id}
                data-testid="enq-modal-delete"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
